import React, { useState, useEffect } from 'react';

const DiscoveryQuestions = ({ onResponseChange, initialResponses = {} }) => {
  const [responses, setResponses] = useState(initialResponses);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  const discoveryQuestions = [
    {
      id: 'flow-state',
      question: 'Think about a time when you totally lost track of time. What were you doing?',
      insight: 'FLOW STATE',
      why: 'This reveals your natural engagement patterns and intrinsic motivations',
      example: 'Could be: researching something random online, fixing something broken, helping someone solve a problem, creating something, debating an idea...',
      placeholder: "Don't overthink it - what activity makes hours feel like minutes?"
    },
    {
      id: 'values',
      question: "What's a problem that really bothers you?",
      insight: 'VALUES',
      why: 'This uncovers your core values and where you\'d find meaning in making impact',
      example: 'What makes you think "that\'s not fair" or "someone should fix this" or "why doesn\'t this work better?"',
      placeholder: 'What injustice, inefficiency, or suffering genuinely bugs you?'
    },
    {
      id: 'natural-strengths',
      question: 'When friends or family have a problem, what do they come to you for help with?',
      insight: 'NATURAL STRENGTHS',
      why: 'This reveals strengths you might take for granted - your natural superpowers',
      example: 'Tech problems? Research help? Talking through decisions? Creative ideas? Organizing stuff? Being a voice of reason?',
      placeholder: 'What do people naturally turn to you for? What comes easily to you that others struggle with?'
    },
    {
      id: 'motivation-drivers',
      question: 'Describe a moment when you felt genuinely proud of something you accomplished.',
      insight: 'MOTIVATION DRIVERS',
      why: 'This reveals what types of achievement actually satisfy you vs. what you think should',
      example: 'Was it solving something hard? Helping someone? Creating something? Leading a team? Learning something new? Making a difference?',
      placeholder: "Think of a specific moment when you thought 'I'm really proud of what I just did'..."
    },
    {
      id: 'aspirational-identity',
      question: 'If you could shadow someone for a week (any job, any person), who would it be and why?',
      insight: 'ASPIRATIONAL IDENTITY',
      why: 'This uncovers who you admire and what kind of person you want to become',
      example: 'Don\'t just think "doctor" - think about what KIND of doctor doing WHAT specifically, and WHY that appeals to you',
      placeholder: 'Be specific about the person and what you\'d want to learn from watching them work...'
    },
    {
      id: 'unique-perspective',
      question: "What's something you believe that most people your age don't seem to care about?",
      insight: 'UNIQUE PERSPECTIVE',
      why: 'This reveals your distinctive interests and where you might find less obvious opportunities',
      example: 'Could be about technology, healthcare, animals, society, the environment, learning, creativity...',
      placeholder: 'What do you think is important that others seem to ignore or not understand?'
    },
    {
      id: 'learning-style',
      question: 'How do you prefer to learn something difficult?',
      insight: 'LEARNING STYLE',
      why: 'This helps match you with programs that teach in ways that actually work for your brain',
      example: 'Hands-on experiments? Reading everything first? Talking it through? Trial and error? Having someone show you? Working with others?',
      placeholder: 'Think about when you successfully learned something hard - how did you do it?'
    },
    {
      id: 'legacy-values',
      question: 'What would you want to be remembered for if you could only accomplish one big thing in your life?',
      insight: 'LEGACY VALUES',
      why: 'This reveals your deepest sense of purpose and what would make you feel your life mattered',
      example: 'Saving lives? Advancing knowledge? Making something beautiful? Solving a big problem? Helping a specific group of people?',
      placeholder: 'If you could only make one lasting impact, what would you choose?'
    },
    {
      id: 'environment',
      question: 'When do you do your best work?',
      insight: 'ENVIRONMENT',
      why: 'This reveals your optimal working conditions and program structure preferences',
      example: 'Structured schedule vs. flexible? Quiet library vs. busy coffee shop? Solo vs. with others? Pressure vs. relaxed? Morning vs. night?',
      placeholder: 'Think about when you\'ve produced your best work - what were the conditions?'
    },
    {
      id: 'social-energy',
      question: 'After spending time with people, do you feel energized or drained?',
      insight: 'SOCIAL ENERGY',
      why: 'This helps match you with programs that have the right social intensity level',
      example: 'Love being around people all day? Need regular alone time to recharge? Depends on the people/activity?',
      placeholder: 'Be honest - are you more extroverted or introverted in different situations?'
    },
    {
      id: 'depth-vs-breadth',
      question: 'Would you rather be really good at one thing or pretty good at many things?',
      insight: 'DEPTH VS BREADTH',
      why: 'This reveals whether you thrive with specialization or variety',
      example: 'Deep expertise in one area? Connecting ideas across fields? Jack-of-all-trades? Depends on the topic?',
      placeholder: 'What feels more exciting - mastering something or exploring lots of different things?'
    },
    {
      id: 'risk-tolerance',
      question: 'How do you feel about being wrong or failing at something?',
      insight: 'RISK TOLERANCE',
      why: 'This determines what level of challenge and uncertainty you can handle productively',
      example: 'Hate it and avoid risk? See it as learning? Depends on the stakes? Get motivated by challenge?',
      placeholder: 'Think about your gut reaction when something doesn\'t work out as planned...'
    },
    {
      id: 'role-preference',
      question: 'Do you prefer being the person with the idea or the person who makes it happen?',
      insight: 'ROLE PREFERENCE',
      why: 'This reveals whether you\'re more of a visionary, executor, or collaborator',
      example: 'Come up with creative solutions? Figure out implementation details? Coordinate between people? All of the above?',
      placeholder: 'In group projects, what role do you naturally gravitate toward?'
    },
    {
      id: 'motivation-source',
      question: 'When you accomplish something, do you want recognition or just personal satisfaction?',
      insight: 'MOTIVATION SOURCE',
      why: 'This helps identify programs that reward you in ways that actually matter to you',
      example: 'Love public recognition? Prefer quiet acknowledgment? Just want to know you made a difference? Depends on what it is?',
      placeholder: 'What kind of \'reward\' actually motivates you?'
    },
    {
      id: 'decision-style',
      question: 'Are you more bothered by wasting time or missing an opportunity?',
      insight: 'DECISION STYLE',
      why: 'This reveals your natural approach to choices and commitment',
      example: 'Hate inefficiency and want to optimize? Fear of missing out on something better? Balance both concerns?',
      placeholder: 'When making decisions, what\'s your biggest worry?'
    },
    {
      id: 'physical-environment',
      question: 'Physical space matters: Where do you feel most creative and focused?',
      insight: 'PHYSICAL ENVIRONMENT',
      why: 'This helps match you with programs in environments where you\'ll actually thrive',
      example: 'Outdoors? Modern lab? Cozy office? Hospital setting? Classroom? Home? Depends on the task?',
      placeholder: 'Think about spaces where you feel most \'yourself\' and productive...'
    }
  ];

  // Auto-save functionality
  useEffect(() => {
    const saveTimer = setTimeout(() => {
      if (Object.keys(responses).length > 0) {
        setIsAutoSaving(true);
        onResponseChange(responses);
        setTimeout(() => setIsAutoSaving(false), 1000);
      }
    }, 2000);

    return () => clearTimeout(saveTimer);
  }, [responses, onResponseChange]);

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const getCompletionCount = () => {
    return Object.values(responses).filter(response => response && response.trim().length > 0).length;
  };

  const getProgressPercentage = () => {
    return Math.round((getCompletionCount() / discoveryQuestions.length) * 100);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-emerald-800 mb-2">
          🔍 Discovery Questions - Let's Find What You Didn't Know You Wanted
        </h2>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
          <div className="text-sm font-medium text-emerald-700">
            <strong>💡 No essays required!</strong> Just jot down whatever comes to mind - phrases, bullet points, random thoughts. The messier, the more honest.
          </div>
        </div>
        
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="text-sm text-emerald-600">
            {getCompletionCount()} of {discoveryQuestions.length} completed
          </div>
          <div className="w-48 bg-emerald-100 rounded-full h-2">
            <div 
              className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          <div className="text-sm font-medium text-emerald-600">
            {getProgressPercentage()}%
          </div>
        </div>

        {/* Auto-save indicator */}
        {isAutoSaving && (
          <div className="text-xs text-emerald-600 flex items-center justify-center gap-1">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            Auto-saving...
          </div>
        )}
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {discoveryQuestions.map((question) => (
          <div key={question.id} className="bg-white border-2 border-emerald-100 rounded-xl p-6 hover:border-emerald-200 transition-all hover:shadow-sm">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-emerald-800">
                  {question.question}
                </h3>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                  {question.insight}
                </span>
              </div>
              
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-3">
                <p className="text-sm text-emerald-700 italic">
                  {question.why}
                </p>
              </div>
              
              <p className="text-sm text-emerald-600 mb-4">
                {question.example}
              </p>
            </div>

            <textarea
              value={responses[question.id] || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              className="w-full p-4 bg-emerald-25 border-2 border-dashed border-emerald-200 rounded-lg focus:border-emerald-300 focus:border-solid focus:outline-none resize-y min-h-[80px] text-sm placeholder-emerald-400"
            />
            
            {responses[question.id] && responses[question.id].trim().length > 0 && (
              <div className="mt-2 flex items-center gap-1 text-xs text-emerald-600">
                <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                Response saved
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      {getCompletionCount() > 0 && (
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-emerald-800 mb-3">
            Your Discovery Progress
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium text-emerald-700 mb-2">Completed Areas:</div>
              {discoveryQuestions
                .filter(q => responses[q.id] && responses[q.id].trim().length > 0)
                .map(q => (
                  <div key={q.id} className="flex items-center gap-2 text-emerald-600">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                    {q.insight}
                  </div>
                ))
              }
            </div>
            <div>
              <div className="font-medium text-emerald-700 mb-2">Still to Explore:</div>
              {discoveryQuestions
                .filter(q => !responses[q.id] || responses[q.id].trim().length === 0)
                .slice(0, 5)
                .map(q => (
                  <div key={q.id} className="flex items-center gap-2 text-emerald-500">
                    <div className="w-2 h-2 border border-emerald-500 rounded-full" />
                    {q.insight}
                  </div>
                ))
              }
              {discoveryQuestions.filter(q => !responses[q.id] || responses[q.id].trim().length === 0).length > 5 && (
                <div className="text-emerald-500">
                  ... and {discoveryQuestions.filter(q => !responses[q.id] || responses[q.id].trim().length === 0).length - 5} more
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscoveryQuestions;